import type {TravelType} from '@/modules/common/context/QueryParams/models';
import type {DeepPartial} from '@/types';
import type {SearchFormConfig} from '@travel-frontend/ui-components';

import {useSearchFormDefaultConfig} from '@travel-frontend/ui-components';
import {merge} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import keywordApi from '@/api/keywordApi';
import {config} from '@/config';
import {useConfig} from '@/modules/common/context/Config';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {useLocalSplit} from '@/modules/common/hooks';
import {useServiceConfig} from '@/modules/common/hooks/useServiceConfig';

type UseSearchFormConfigProps = {
  travelType: TravelType;
  config?: DeepPartial<SearchFormConfig>;
};

type UseSearchFormConfigReturnValue = SearchFormConfig;
const topRegionsAsListSplitTreatment = {
  control: 'default',
  a: 'default',
  b: 'compact',
  c: 'list',
  d: 'default',
} as const;

export const useSearchFormConfig = ({
  travelType,
  config: configProperty,
}: UseSearchFormConfigProps): UseSearchFormConfigReturnValue => {
  const {t} = useTranslation('general');
  const {configService: configServiceConfig, pageService: pageServiceConfig} =
    useConfig();
  const serviceConfig = useServiceConfig();
  const lastSearchesSplitEnabled =
    useLocalSplit('FSE-3287_last_searches').treatment === 'on';
  const durationPickerSplitEnabled =
    useLocalSplit('FSE_3060_new_datepicker').treatment === 'on';
  const showHolidayDataSplitEnabled =
    useLocalSplit('FSE_3296_show_vacations').treatment === 'on';
  const allowEmptySearchForm =
    useLocalSplit<'variation' | 'controlgroup'>(
      'FSE_3513_allow_empty_searchform_submit'
    ).treatment === 'variation';

  const {treatment: topRegionTreatment} = useLocalSplit<
    keyof typeof topRegionsAsListSplitTreatment
  >('FSE_3299_top_regions_as_list');
  const topRegionsListType = topRegionsAsListSplitTreatment[topRegionTreatment];
  const fetchDestinations = useCallback(
    ({
      searchTerm,
      abortSignal,
    }: {
      searchTerm: string;
      abortSignal: AbortSignal;
    }): Promise<any> =>
      keywordApi
        .autocomplete(
          {
            q: searchTerm,
            travelTypes: [travelType],
          },
          {signal: abortSignal}
        )
        .then((response) => {
          return ('items' in response.data ? response.data.items : [])
            .filter((item) => item !== null)
            .map((item) => ({
              ...item,
              meta: {
                ...item.meta,
                ...(config.searchForm.categoryType
                  ? {categoryType: config.searchForm.categoryType}
                  : null),
              },
            }));
        }),
    [travelType]
  );

  const travelTypeFieldOptions = useMemo(() => {
    return config.searchForm.travelTypeField.options?.map(({type}) => {
      return {
        label: t(`searchFormSwitcher.${type}`),
        value: type,
      };
    });
  }, [t]);

  const handleTravelTypeFieldChange = useCallback((value: string) => {
    const selectedTravelType = config.searchForm?.travelTypeField?.options.find(
      ({type}) => type === value
    );

    if (selectedTravelType?.url) {
      window.open(selectedTravelType.url, '_blank');
    }
  }, []);

  return useSearchFormDefaultConfig({
    // @ts-expect-error ...
    config: useMemo(
      () =>
        merge(
          {},
          {
            submitButtonLabel: 'adjustSearch',
            travelTypeField: {
              options: travelTypeFieldOptions,
              onChange: handleTravelTypeFieldChange,
              mode: 'denseList',
            },
            airportsField: {
              airports: config.airports,
              countryCode: import.meta.env.VITE_PORTAL_TLD,
            },
            destinationField: {
              required: !allowEmptySearchForm,
              fetchDestinations,
              templateItems: Array.isArray(pageServiceConfig?.blocks)
                ? pageServiceConfig.blocks
                : [],
              recentSearches: lastSearchesSplitEnabled,
              topRegionsListType,
            },
            travellersField: {
              multiRoomBooking: serviceConfig.multiRoomBooking,
            },
            travelPeriodField: {
              durationOptions: config.durationOptions[travelType],
              durationOptionsNew:
                travelType === TravelTypes.packageTour
                  ? config.durationOptionsPackageNew
                  : config.durationOptionsOwnArrivalNew,
              holidayDates: configServiceConfig?.holidays,
              showHolidays: showHolidayDataSplitEnabled,
            },
            travellersFieldMultiroom: {
              serviceConfig: {
                defaultOpeningHours: serviceConfig.defaultOpeningHours,
                multiRoomBooking: serviceConfig.multiRoomBooking,
                phone: serviceConfig.phone,
                specialOpeningHours: serviceConfig.specialOpeningHours,
              },
            },
            hasNextDP: durationPickerSplitEnabled,
          },
          configProperty
        ),
      [
        lastSearchesSplitEnabled,
        durationPickerSplitEnabled,
        showHolidayDataSplitEnabled,
        travelTypeFieldOptions,
        handleTravelTypeFieldChange,
        fetchDestinations,
        travelType,
        configProperty,
        serviceConfig,
        topRegionsListType,
        allowEmptySearchForm,
        configServiceConfig,
        pageServiceConfig,
      ]
    ),
  });
};
